import React from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  LocalOffer as PriceIcon,
  Schedule as DurationIcon,
  Event as AppointmentIcon,
  EventAvailable as RecentIcon,
  MonetizationOn as RevenueIcon
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useServiceDetails } from '../../hooks/useServices';
import DevOnly from '../../components/DevOnly';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { data: service, isLoading, error, refetch } = useServiceDetails(serviceId);

  const formatPrice = React.useCallback((price, options = {}) => {
    const { treatZeroAsFree = true, freeLabel = 'Free' } = options;
    if (price === null || price === undefined) return 'Not set';
    const numeric = Number(price);
    if (!Number.isFinite(numeric)) return 'Not set';
    if (numeric === 0 && treatZeroAsFree) return freeLabel;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD'
      }).format(numeric);
    } catch (err) {
      return `$${numeric.toFixed(2)}`;
    }
  }, []);

  const formatDuration = React.useCallback((duration) => {
    if (!duration && duration !== 0) return 'Not specified';
    const minutes = Number(duration);
    if (Number.isNaN(minutes) || minutes <= 0) return 'Not specified';
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    if (!hours) return `${remaining} min`;
    if (!remaining) return `${hours} hr${hours > 1 ? 's' : ''}`;
    return `${hours} hr${hours > 1 ? 's' : ''} ${remaining} min`;
  }, []);

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch (err) {
      return value;
    }
  };

  const renderColorSwatch = (color) => {
    if (!color) return '—';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: color
          }}
        />
        <Typography variant="body2">{color}</Typography>
      </Box>
    );
  };

  const DetailRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25, gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {React.isValidElement(value) ? (
        value
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value ?? '—'}
        </Typography>
      )}
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load service details.
        </Alert>
      </Box>
    );
  }

  if (!service) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Service not found.</Alert>
      </Box>
    );
  }

  const stats = [
    {
      label: 'Price',
      value: formatPrice(service.price),
      icon: <PriceIcon color="primary" sx={{ mr: 1 }} />
    },
    {
      label: 'Duration',
      value: formatDuration(service.duration),
      icon: <DurationIcon color="secondary" sx={{ mr: 1 }} />
    },
    {
      label: 'Appointments',
      value: service.appointment_count || 0,
      icon: <AppointmentIcon color="warning" sx={{ mr: 1 }} />
    },
    {
      label: '30 Day Appointments',
      value: service.recent_appointments || 0,
      icon: <RecentIcon color="info" sx={{ mr: 1 }} />
    }
  ];

  if (service.revenue) {
    stats.push({
      label: 'Lifetime Revenue',
      value: formatPrice(service.revenue, { treatZeroAsFree: false }),
      icon: <RevenueIcon color="success" sx={{ mr: 1 }} />
    });
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/services" color="inherit">
          Services
        </Link>
        {service.category_name && (
          <Link component={RouterLink} to={`/services/category/${service.category_id}`} color="inherit">
            {service.category_name}
          </Link>
        )}
        <Typography color="text.primary">{service.service_name}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {service.service_name}
            </Typography>
            <Chip
              label={service.is_active ? 'Active' : 'Inactive'}
              color={service.is_active ? 'success' : 'default'}
              variant={service.is_active ? 'filled' : 'outlined'}
            />
            {service.category_name && (
              <Chip
                label={service.category_name}
                variant="outlined"
                color="primary"
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Service ID #{service.service_id} {service.pabau_id ? `• Pabau ${service.pabau_id}` : ''}
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/services/edit/${service.id}`)}
          >
            Edit Service
          </Button>
        </DevOnly>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.label}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {stat.icon}
                  <Typography variant="h6">{stat.value}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              General Details
            </Typography>
            <Divider />
            <DetailRow label="Category Code" value={service.category_code || '—'} />
            <DetailRow label="Online Booking" value={service.online_booking ? 'Enabled' : 'Disabled'} />
            <DetailRow label="Color" value={renderColorSwatch(service.color)} />
            <DetailRow label="Created" value={formatDate(service.created_date)} />
            <DetailRow
              label="Updated"
              value={service.last_updated ? formatDistanceToNow(new Date(service.last_updated), { addSuffix: true }) : '—'}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Booking Insights
            </Typography>
            <Divider />
            <DetailRow label="Appointments" value={service.appointment_count || 0} />
            <DetailRow label="30 Day Appointments" value={service.recent_appointments || 0} />
            <DetailRow label="Lifetime Revenue" value={formatPrice(service.revenue || 0, { treatZeroAsFree: false, freeLabel: '$0.00' })} />
            <DetailRow label="Duration" value={formatDuration(service.duration)} />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Description
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {service.description ? (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {service.description}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No description available for this service.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ServiceDetail;
